import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { HoverCardPortal } from "@radix-ui/react-hover-card";
import { useTranslation } from "react-i18next";
import type { UserMeta } from "@/lib/render-items";
import {
  formatPreciseDate,
  MessageMetaRows,
  parseMessageDate,
} from "@/components/ai-elements/message-timestamp";

function formatTimeOnly(timestamp: string, locale: string): string {
  const date = parseMessageDate(timestamp);
  if (!date) return timestamp;

  try {
    return new Intl.DateTimeFormat(locale, {
      hour: "numeric",
      minute: "2-digit",
    }).format(date);
  } catch {
    return timestamp;
  }
}

function initialsFromName(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => Array.from(part)[0] ?? "")
    .join("")
    .toUpperCase();
}

/**
 * Visible sender and timestamp metadata for ``<user_meta>``-tagged user
 * messages. The hover card retains secondary details: elapsed time since the
 * last message and the selected response language.
 *
 * Only rendered for web user messages that have the ``<user_meta>`` tag.
 * Assistant timestamps use their own metadata source and component.
 */
export function MessageMetaHover({ userMeta }: { userMeta: UserMeta }) {
  const { t, i18n } = useTranslation();
  const senderName = userMeta.name?.trim();
  const locale = i18n.resolvedLanguage || i18n.language;
  const date = parseMessageDate(userMeta.timestamp);
  const precise = date ? formatPreciseDate(date, locale) : userMeta.timestamp;

  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-end gap-1.5 rounded-sm text-[11px] text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={t("messageMeta.showTimestamp", { timestamp: precise })}
          data-testid="message-sender-meta"
        >
          {senderName && (
            <>
              <span
                aria-hidden="true"
                className="flex size-4 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[9px] font-semibold text-primary"
                data-testid="message-sender-avatar"
              >
                {initialsFromName(senderName)}
              </span>
              <span className="max-w-40 truncate font-medium text-foreground">
                {senderName}
              </span>
            </>
          )}
          <span className="tabular-nums">{formatTimeOnly(userMeta.timestamp, locale)}</span>
        </button>
      </HoverCardTrigger>
      <HoverCardPortal>
        <HoverCardContent
          align="end"
          sideOffset={4}
          className="w-72 max-w-[calc(100vw-2rem)] bg-popover p-4 text-popover-foreground shadow-lg"
        >
          <MessageMetaRows rows={[
            ...(userMeta.name ? [{ label: t("messageMeta.sentBy"), value: userMeta.name }] : []),
            { label: t("messageMeta.time"), value: precise, tabular: true },
            ...(userMeta.elapsedSinceLastMsg ? [{ label: t("messageMeta.sinceLast"), value: userMeta.elapsedSinceLastMsg }] : []),
            ...(userMeta.localeName ? [{ label: t("messageMeta.language"), value: userMeta.localeName }] : []),
          ]} />
        </HoverCardContent>
      </HoverCardPortal>
    </HoverCard>
  );
}
